import React, { useMemo } from 'react'; // Removed useState
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import styles from './MetricLineChart.module.css';
// Assuming Skeleton path is correct and the component is exactly as you provided
import Skeleton from '../Skeleton/Skeleton';
import Flex from '../Flex/Flex';
import { formatValue } from '@/utils/formatUtils';
import dayjs from 'dayjs';
import { dayjsInUTC } from '@/utils/dateUtils';

// --- Reusable Icon ---
// (Using the same UpArrowIcon definition as before, assuming it's available/imported)
const UpArrowIcon = () => (
    <svg
        width="14" // Adjusted size slightly
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginRight: '2px', verticalAlign: 'middle', color: 'var(--static-content-positive, #16A34A)' }} // Use CSS var for color
    >
        <path
            d="M12 4L12 20M12 4L18 10M12 4L6 10"
            stroke="currentColor"
            strokeWidth="2.5" // Make slightly bolder if needed
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// --- Types (mostly unchanged) ---
type MetricData = {
    [key: string]: number | string;
    day?: number;
};

type MetricTotal = {
    value: number;
    change?: {
        value: number;
        label: string;
    };
};

type Metric = {
    key: string;
    label: string;
    color: string;
    formatter?: (value: number) => string; // Metric-specific formatter
};

type MetricLineChartProps = {
    data: MetricData[] | null | undefined;
    metrics: Metric[] | null | undefined;
    totals: Record<string, MetricTotal> | null | undefined;
    valueFormatter?: (value: number) => string; // General formatter
    xAxisKey?: string;
    xAxisFormatter?: (value: any) => string;
    isLoading?: boolean;
    className?: string; // Allow passing custom class
    height?: string; // Allow customizing height
};

// --- Default Formatters ---
const defaultCurrencyFormatter = (value: number): string => {
    if (typeof value !== 'number' || isNaN(value)) {
        return '$--';
    }
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// --- Custom Tooltip Component (Refactored) ---
const CustomChartTooltip = ({
    active,
    payload,
    label,
    metrics,
    valueFormatter,
    xAxisKey
}: TooltipProps<ValueType, NameType> & { metrics?: Metric[], valueFormatter: (value: number) => string, xAxisKey: string }) => {

    if (active && payload && payload.length && metrics) {
        const currentPayload = payload[0]?.payload;

        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>
                    {currentPayload && xAxisKey in currentPayload
                        ? `${dayjs(currentPayload[xAxisKey]).format('MMM DD YYYY')}`
                        : `${label}`}
                </p>
                {payload.map((item) => {
                    const metricConfig = metrics.find(m => m.key === item.dataKey);
                    if (!metricConfig) return null;

                    const formatValue = metricConfig.formatter || valueFormatter;
                    const formattedValue = typeof item.value === 'number' ? formatValue(item.value) : String(item.value ?? '--');

                    return (
                        <p key={item.dataKey} className={styles.tooltipValue}>
                            <span style={{ color: metricConfig.color, marginRight: '4px' }}>●</span>
                            {`${metricConfig.label}: `}
                            <span className={styles.tooltipMetricValue}>{formattedValue}</span>
                        </p>
                    );
                })}
            </div>
        );
    }
    return null;
};

const getXAxisConfig = (data: any[]) => {
    const totalPoints = data.length;
    const firstDate = dayjsInUTC(data[0].day);
    const lastDate = dayjsInUTC(data[data.length - 1].day);
    const diffInYears = lastDate.diff(firstDate, 'year', true);

    // For data spanning more than a year
    if (diffInYears > 1) {
        return {
            // If we have 365 points and want 12 ticks
            // interval would be Math.floor(365/12) - 1 ≈ 29
            interval: Math.max(Math.floor(totalPoints / 12), 0),
            formatter: (day: number) => dayjsInUTC(day).format('MMM YYYY')
        };
    }
    
    // For shorter periods, show fewer points
    return {
        interval: Math.max(Math.floor(totalPoints / 24), 0),
        formatter: (day: number) => dayjsInUTC(day).format('MMM D')
    };
}

// --- Main Component (Refactored) ---
const MetricLineChart = ({
    data,
    metrics,
    totals,
    valueFormatter = defaultCurrencyFormatter,
    xAxisKey = "day",
    isLoading = false,
    className = '',
    height = "224px", // Default height
}: MetricLineChartProps) => {

    // Memoize metrics lookup
    const metricsMap = useMemo(() => {
        if (!metrics) return {};
        return metrics.reduce((acc, metric) => {
            acc[metric.key] = metric;
            return acc;
        }, {} as Record<string, Metric>);
    }, [metrics]);

    // Basic validation
    const hasValidData = data && data.length > 0;
    const hasValidMetrics = metrics && metrics.length > 0;
    const hasValidTotals = totals && Object.keys(totals).length > 0;

    // Render Skeleton or No Data Message
    if (isLoading) {
        // *** Restored Original Skeleton Usage ***
        // Render a single large skeleton placeholder as in the initial code.
        // This does not attempt to mimic the two-column structure during loading,
        // as the original Skeleton component doesn't have props like `count` or complex layout support built-in.
        return (
             <div className={`${styles.loadingContainer}`}>
                <Skeleton width='100%' height={height} />
             </div>
        );
        // *****************************************
    }

    return (
        <div className={`${styles.container} ${className}`}>
            <div className={styles.grid}>
                {/* Stats column */}
                <div className={styles.statsColumn}>
                    {hasValidTotals && metrics.map((metric) => {
                        const totalData = totals![metric.key];
                        if (!totalData) return null;

                        const formattedTotal = formatValue(totalData.value, 'currency');

                        return (
                            <div key={metric.key} className={styles.metricGroup}>
                                <div className={styles.labelGroup}>
                                    <div className={styles.dot} style={{ backgroundColor: metric.color }}></div>
                                    <span className={styles.label}>{metric.label}</span>
                                </div>
                                <p className={styles.value}>
                                    {formattedTotal}
                                </p>
                                {totalData.change && (
                                    <p className={styles.change}>
                                        <UpArrowIcon />
                                        {Math.abs(totalData.change.value)}%
                                        <span className={styles.changeLabel}>{totalData.change.label}</span>
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    {!hasValidTotals && <p className={styles.noTotals}>Summary data unavailable.</p>}
                </div>

                {/* Chart column */}
                <div className={styles.chartContainer} style={{ height: height }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={data}
                            margin={{ top: 5, right: 15, left: -15, bottom: 0 }} // Adjusted margins
                        >
                            <XAxis
                                dataKey={xAxisKey}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'var(--static-text-secondary, #9CA3AF)', fontSize: 12 }}
                                tickFormatter={getXAxisConfig(data).formatter}
                                interval={getXAxisConfig(data).interval}
                                // dy={5}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: 'var(--static-text-secondary, #9CA3AF)', fontSize: 12 }}
                                tickFormatter={valueFormatter}
                                width={60}
                                dx={-5}
                            />
                            <Tooltip
                                content={
                                    <CustomChartTooltip
                                        metrics={metrics}
                                        valueFormatter={valueFormatter}
                                        xAxisKey={xAxisKey}
                                    />
                                }
                                cursor={{ stroke: 'var(--static-divider-standard, #E5E7EB)', strokeWidth: 1, strokeDasharray: '3 3' }}
                            />
                            {metrics.map((metric) => (
                                <Line
                                    key={metric.key}
                                    type="monotone"
                                    dataKey={metric.key}
                                    stroke={metric.color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{
                                        stroke: metric.color,
                                        strokeWidth: 2,
                                        r: 4,
                                        fill: 'var(--static-background-elevated, #FFFFFF)'
                                     }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default MetricLineChart;